import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings, User, CreditCard, Download, Trash2, Save, Users, Plus, Edit2, X, Baby, Heart, LogOut } from "lucide-react";;
import { toast } from 'sonner';
import useStore from '../store';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIET_OPTIONS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten-free', 'lactose-free', 'low-carb', 'keto'];
const CUISINE_OPTIONS = ['italian', 'turkish', 'moroccan', 'middle_eastern', 'dutch', 'german', 'asian', 'indian', 'mexican', 'french', 'spanish'];
const ALLERGY_OPTIONS = ['peanuts', 'tree-nuts', 'dairy', 'eggs', 'shellfish', 'fish', 'wheat', 'soy', 'sesame'];

function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser, logout } = useStore();
  const [household, setHousehold] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);

  const [profileForm, setProfileForm] = useState({ name: '', country: '' });
  const [householdForm, setHouseholdForm] = useState({ aantal_personen: 2, household_goal: 'maintain' });
  const [memberForm, setMemberForm] = useState({
    name: '', age: 30, gender: 'other', goal: 'maintain',
    religious_diet: 'none', halal_level: 'flexible', ramadan_active: false,
    diet_tags: [], allergies: [], cuisine_preferences: [],
    is_baby: false, is_pregnant: false, pregnancy_trimester: 'none'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [householdRes, trialRes] = await Promise.all([
          axios.get(`${API}/households`, { withCredentials: true }),
          axios.get(`${API}/users/trial-status`, { withCredentials: true })
        ]);
        const hh = householdRes.data.household;
        setHousehold(hh);
        setTrialStatus(trialRes.data);
        if (hh) {
          setHouseholdForm({
            aantal_personen: hh.aantal_personen || 2,
            household_goal: hh.household_goal || 'maintain'
          });
          // Fetch family members
          const membersRes = await axios.get(`${API}/family-members/${hh.household_id}`, { withCredentials: true });
          setFamilyMembers(membersRes.data.members || []);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', country: user.country || '' });
    }
  }, [user]);

  const handleLogout = async () => {
  try {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    logout();
    navigate('/login');
  }
};
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(`${API}/users/profile`, profileForm, { withCredentials: true });
      if (response.data.user) {
        setUser({ ...user, ...response.data.user });
      }
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHousehold = async () => {
    if (!household) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/households/${household.household_id}`, householdForm, { withCredentials: true });
      setHousehold({ ...household, ...householdForm });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      await axios.post(`${API}/users/start-trial`, {}, { withCredentials: true });
      toast.success(t('common.success'));
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const resetMemberForm = () => {
    setMemberForm({
      name: '', age: 30, gender: 'other', goal: 'maintain',
      religious_diet: 'none', halal_level: 'flexible', ramadan_active: false,
      diet_tags: [], allergies: [], cuisine_preferences: [],
      is_baby: false, is_pregnant: false, pregnancy_trimester: 'none'
    });
  };

  const handleAddMember = async () => {
    if (!household || !memberForm.name.trim()) {
      toast.error(t('settings.member_name_required'));
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/family-members`, {
        ...memberForm,
        household_id: household.household_id
      }, { withCredentials: true });
      
      // Refresh members list
      const membersRes = await axios.get(`${API}/family-members/${household.household_id}`, { withCredentials: true });
      setFamilyMembers(membersRes.data.members || []);
      
      setShowAddMember(false);
      resetMemberForm();
      toast.success(t('settings.member_added'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member.member_id);
    setMemberForm({
      name: member.name || '',
      age: member.age || 30,
      gender: member.gender || 'other',
      goal: member.goal || 'maintain',
      religious_diet: member.religious_diet || 'none',
      halal_level: member.halal_level || 'flexible',
      ramadan_active: member.ramadan_active || false,
      diet_tags: member.diet_tags || [],
      allergies: member.allergies || [],
      cuisine_preferences: member.cuisine_preferences || member.cuisines || [],
      is_baby: member.is_baby || false,
      is_pregnant: member.is_pregnant || false,
      pregnancy_trimester: member.pregnancy_trimester || 'none'
    });
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/family-members/${editingMember}`, memberForm, { withCredentials: true });
      
      // Refresh members list
      const membersRes = await axios.get(`${API}/family-members/${household.household_id}`, { withCredentials: true });
      setFamilyMembers(membersRes.data.members || []);
      
      setEditingMember(null);
      resetMemberForm();
      toast.success(t('settings.member_updated'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm(t('settings.confirm_delete_member'))) return;
    try {
      await axios.delete(`${API}/family-members/${memberId}`, { withCredentials: true });
      setFamilyMembers(familyMembers.filter(m => m.member_id !== memberId));
      toast.success(t('settings.member_deleted'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const toggleArrayItem = (field, item) => {
    const current = memberForm[field] || [];
    if (current.includes(item)) {
      setMemberForm({ ...memberForm, [field]: current.filter(i => i !== item) });
    } else {
      setMemberForm({ ...memberForm, [field]: [...current, item] });
    }
  };

  const MemberFormFields = () => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>{t('settings.member_name')}</Label>
          <Input
            value={memberForm.name}
            onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
            placeholder={t('settings.member_name')}
            data-testid="member-name-input"
          />
        </div>
        <div>
          <Label>{t('settings.member_age')}</Label>
          <Input
            type="number"
            min={0}
            max={120}
            value={memberForm.age}
            onChange={(e) => setMemberForm({ ...memberForm, age: parseInt(e.target.value) || 0 })}
            data-testid="member-age-input"
          />
        </div>
        <div>
          <Label>{t('settings.member_gender')}</Label>
          <Select value={memberForm.gender} onValueChange={(v) => setMemberForm({ ...memberForm, gender: v })}>
            <SelectTrigger data-testid="member-gender-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t('settings.male')}</SelectItem>
              <SelectItem value="female">{t('settings.female')}</SelectItem>
              <SelectItem value="other">{t('settings.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Special Conditions */}
      <div className="grid md:grid-cols-2 gap-4 p-4 bg-pink-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Baby className="w-4 h-4 text-pink-500" />
            <Label>{t('settings.is_baby')}</Label>
          </div>
          <Switch
            checked={memberForm.is_baby}
            onCheckedChange={(v) => setMemberForm({ ...memberForm, is_baby: v })}
            data-testid="member-baby-switch"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" />
            <Label>{t('pregnancy.label')}</Label>
          </div>
          <Switch
            checked={memberForm.is_pregnant}
            onCheckedChange={(v) => setMemberForm({ ...memberForm, is_pregnant: v, pregnancy_trimester: v ? 'first' : 'none' })}
            data-testid="member-pregnant-switch"
          />
        </div>
        {memberForm.is_pregnant && (
          <div className="col-span-2">
            <Label>{t('pregnancy.trimester_label')}</Label>
            <Select value={memberForm.pregnancy_trimester} onValueChange={(v) => setMemberForm({ ...memberForm, pregnancy_trimester: v })}>
              <SelectTrigger data-testid="member-trimester-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="first">{t('pregnancy.first')}</SelectItem>
                <SelectItem value="second">{t('pregnancy.second')}</SelectItem>
                <SelectItem value="third">{t('pregnancy.third')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-pink-600 mt-1">{t('pregnancy.disclaimer')}</p>
          </div>
        )}
      </div>

      {/* Religious Diet */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>{t('settings.religious_diet')}</Label>
          <Select value={memberForm.religious_diet} onValueChange={(v) => setMemberForm({ ...memberForm, religious_diet: v })}>
            <SelectTrigger data-testid="member-religion-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('settings.no_religious_diet')}</SelectItem>
              <SelectItem value="islam">{t('settings.islam')}</SelectItem>
              <SelectItem value="jewish">{t('settings.jewish')}</SelectItem>
              <SelectItem value="christian">{t('settings.christian')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {memberForm.religious_diet === 'islam' && (
          <>
            <div>
              <Label>{t('settings.halal_level')}</Label>
              <Select value={memberForm.halal_level} onValueChange={(v) => setMemberForm({ ...memberForm, halal_level: v })}>
                <SelectTrigger data-testid="member-halal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flexible">{t('settings.halal_flexible')}</SelectItem>
                  <SelectItem value="strict">{t('settings.halal_strict')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between col-span-2 p-3 bg-green-50 rounded">
              <Label>{t('settings.ramadan_active')}</Label>
              <Switch
                checked={memberForm.ramadan_active}
                onCheckedChange={(v) => setMemberForm({ ...memberForm, ramadan_active: v })}
                data-testid="member-ramadan-switch"
              />
            </div>
          </>
        )}
      </div>

      {/* Diet Tags */}
      <div>
        <Label>{t('settings.diet_preferences')}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {DIET_OPTIONS.map((diet) => (
            <button
              key={diet}
              type="button"
              onClick={() => toggleArrayItem('diet_tags', diet)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                memberForm.diet_tags.includes(diet)
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid={`diet-tag-${diet}`}
            >
              {t(`recipes.${diet}`) || diet}
            </button>
          ))}
        </div>
      </div>

      {/* Allergies */}
      <div>
        <Label>{t('settings.allergies')}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {ALLERGY_OPTIONS.map((allergy) => (
            <button
              key={allergy}
              type="button"
              onClick={() => toggleArrayItem('allergies', allergy)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                memberForm.allergies.includes(allergy)
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid={`allergy-tag-${allergy}`}
            >
              {allergy}
            </button>
          ))}
        </div>
      </div>

      {/* Cuisine Preferences */}
      <div>
        <Label>{t('settings.cuisine_preferences')}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {CUISINE_OPTIONS.map((cuisine) => (
            <button
              key={cuisine}
              type="button"
              onClick={() => toggleArrayItem('cuisine_preferences', cuisine)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                memberForm.cuisine_preferences.includes(cuisine)
                  ? 'bg-secondary text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              data-testid={`cuisine-tag-${cuisine}`}
            >
              {t(`cuisines.${cuisine}`) || cuisine}
            </button>
          ))}
        </div>
      </div>

      {/* Goal */}
      <div>
        <Label>{t('settings.member_goal')}</Label>
        <Select value={memberForm.goal} onValueChange={(v) => setMemberForm({ ...memberForm, goal: v })}>
          <SelectTrigger data-testid="member-goal-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="maintain">{t('settings.normal')}</SelectItem>
            <SelectItem value="lose">{t('settings.lose')}</SelectItem>
            <SelectItem value="gain">{t('settings.gain')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-4xl">
        <div className="flex items-center gap-3 mb-4 sm:mb-8">
          <div className="bg-primary rounded-full p-2 sm:p-3">
            <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="settings-title">
            {t('settings.title')}
          </h1>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('settings.profile')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('settings.name')}</Label>
                <Input 
                  value={profileForm.name} 
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <Label>{t('settings.email')}</Label>
                <Input value={user?.email || ''} disabled className="bg-gray-50" data-testid="settings-email-input" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('settings.country')}</Label>
                  <Input 
                    value={profileForm.country} 
                    onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                    data-testid="settings-country-input"
                  />
                </div>
                <div>
                  <Label>{t('settings.language')}</Label>
                  <LanguageSelector />
                </div>
              </div>
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="bg-primary hover:bg-primary-hover text-white"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </CardContent>
          </Card>

          <Card data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t('settings.subscription')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-semibold capitalize">{user?.tier || 'free'} Plan</p>
                  {trialStatus?.trial_active && (
                    <p className="text-sm text-gray-600">{t('settings.trial_days_left', { days: trialStatus.days_left })}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {user?.tier === 'free' && !trialStatus?.trial_active && !trialStatus?.has_premium && (
                    <Button onClick={handleStartTrial} data-testid="start-trial-btn">{t('settings.start_trial')}</Button>
                  )}
                  {(user?.tier === 'free' || user?.tier === 'premium') && (
                    <Button onClick={() => navigate('/pricing')} variant="outline" data-testid="upgrade-btn">{t('settings.upgrade')}</Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Family Members Section */}
          {household && (
            <Card data-testid="family-members-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    {t('settings.family_members')}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { setShowAddMember(true); resetMemberForm(); }}
                    className="bg-secondary hover:bg-secondary-hover text-white"
                    data-testid="add-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('settings.add_member')}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add New Member Form */}
                {showAddMember && (
                  <div className="p-4 border-2 border-dashed border-primary rounded-lg bg-primary/5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold">{t('settings.new_member')}</h4>
                      <Button variant="ghost" size="sm" onClick={() => setShowAddMember(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <MemberFormFields />
                    <div className="flex gap-2 mt-4">
                      <Button onClick={handleAddMember} disabled={saving} className="bg-primary text-white" data-testid="save-new-member-btn">
                        <Save className="w-4 h-4 mr-2" />
                        {t('common.save')}
                      </Button>
                      <Button variant="outline" onClick={() => setShowAddMember(false)}>
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Existing Members List */}
                {familyMembers.length === 0 && !showAddMember ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{t('settings.no_members')}</p>
                    <p className="text-sm">{t('settings.add_member_hint')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {familyMembers.map((member) => (
                      <div key={member.member_id} className="p-4 bg-gray-50 rounded-lg">
                        {editingMember === member.member_id ? (
                          <>
                            <MemberFormFields />
                            <div className="flex gap-2 mt-4">
                              <Button onClick={handleUpdateMember} disabled={saving} className="bg-primary text-white" data-testid={`update-member-${member.member_id}`}>
                                <Save className="w-4 h-4 mr-2" />
                                {t('common.save')}
                              </Button>
                              <Button variant="outline" onClick={() => { setEditingMember(null); resetMemberForm(); }}>
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-lg">{member.name}</h4>
                                <span className="text-sm text-gray-500">({member.age} {t('settings.years')})</span>
                                {member.is_baby && <Baby className="w-4 h-4 text-pink-500" />}
                                {member.is_pregnant && <Heart className="w-4 h-4 text-pink-500" />}
                              </div>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {member.diet_tags?.map((tag) => (
                                  <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                                    {tag}
                                  </span>
                                ))}
                                {member.allergies?.map((allergy) => (
                                  <span key={allergy} className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">
                                    {allergy}
                                  </span>
                                ))}
                                {member.religious_diet && member.religious_diet !== 'none' && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded">
                                    {member.religious_diet}
                                  </span>
                                )}
                              </div>
                              {member.cuisine_preferences?.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {member.cuisine_preferences.map((cuisine) => (
                                    <span key={cuisine} className="px-2 py-0.5 bg-secondary/10 text-secondary text-xs rounded">
                                      {t(`cuisines.${cuisine}`) || cuisine}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditMember(member)} data-testid={`edit-member-${member.member_id}`}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteMember(member.member_id)} data-testid={`delete-member-${member.member_id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {household && (
            <Card data-testid="household-card">
              <CardHeader><CardTitle>{t('settings.household')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('settings.persons')}</Label>
                  <Input 
                    type="number" 
                    min={1}
                    max={20}
                    value={householdForm.aantal_personen} 
                    onChange={(e) => setHouseholdForm({ ...householdForm, aantal_personen: parseInt(e.target.value) || 1 })}
                    data-testid="settings-persons-input"
                  />
                  <p className="text-xs text-gray-600 mt-1">{t('settings.persons_desc')}</p>
                </div>
                <div>
                  <Label>{t('settings.household_goal')}</Label>
                  <Select 
                    value={householdForm.household_goal} 
                    onValueChange={(v) => setHouseholdForm({ ...householdForm, household_goal: v })}
                  >
                    <SelectTrigger data-testid="settings-goal-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintain">{t('settings.normal')}</SelectItem>
                      <SelectItem value="lose">{t('settings.lose')}</SelectItem>
                      <SelectItem value="gain">{t('settings.gain')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleSaveHousehold} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary-hover text-white"
                  data-testid="save-household-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? t('common.loading') : t('common.save')}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>{t('settings.data_privacy')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Uitloggen
              </Button>
              <Button variant="outline" className="w-full justify-start" data-testid="export-data-btn">
                <Download className="w-4 h-4 mr-2" />
                {t('settings.export_data')}
              </Button>
              <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-account-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                {t('settings.delete_account')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
